import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as BasePostHogProvider } from '@posthog/react';
import { getPostHogOptions } from './config.js';
import { purgeAnalyticsStorage } from './consent/purge.js';
import { useCookieConsent } from './consent/useCookieConsent.js';
const ConsentModeContext = createContext('respect');
/** The consent mode of the nearest AnalyticsProvider (hooks use it to gate tracking). */
export function useConsentMode() {
    return useContext(ConsentModeContext);
}
/**
 * Flips true once posthog.init has run. Child effects run before the provider's lifecycle effect,
 * so hooks gate on this to avoid identify/capture against the uninitialised client. Defaults to true
 * where no lifecycle exists (analytics disabled), preserving the consent-only gating there.
 */
const PostHogReadyContext = createContext(true);
export function usePostHogReady() {
    return useContext(PostHogReadyContext);
}
/**
 * Drive the PostHog lifecycle from the effective decision. PostHog is initialized lazily on the
 * first `granted` and never while `denied`; `granted → denied` opts out, stops recording and
 * purges storage; `denied → granted` opts back in. `null` (unresolved) does nothing.
 */
function usePostHogLifecycle(apiKey, config, effective, consentMode) {
    const [ready, setReady] = useState(false);
    const initialized = useRef(false);
    const previous = useRef(null);
    const configRef = useRef(config);
    configRef.current = config;
    useEffect(() => {
        if (effective === null)
            return;
        const prev = previous.current;
        previous.current = effective;
        if (effective === 'granted') {
            if (!initialized.current) {
                initialized.current = true;
                const options = getPostHogOptions(configRef.current);
                // Under consent, an opt-out also disables PostHog persistence, so no ph_* key is
                // re-written after the purge (BR-7). Exempt mode keeps today's options unchanged.
                posthog.init(apiKey, consentMode === 'respect' ? { ...options, opt_out_persistence_by_default: true } : options);
                // A persisted opt-out marker from an earlier revoke would keep capture off after init.
                if (posthog.has_opted_out_capturing()) {
                    posthog.opt_in_capturing();
                    posthog.startSessionRecording();
                }
                setReady(true);
            }
            else if (prev === 'denied') {
                posthog.opt_in_capturing();
                posthog.startSessionRecording();
            }
            return;
        }
        if (initialized.current && prev === 'granted') {
            posthog.opt_out_capturing();
            posthog.stopSessionRecording();
        }
        // Revoked now, or decided denied elsewhere on this origin (BR-10): remove identifier storage.
        purgeAnalyticsStorage();
    }, [apiKey, effective, consentMode]);
    return ready;
}
function ConsentRespectingProvider({ children, apiKey, config }) {
    const consent = useCookieConsent();
    const ready = usePostHogLifecycle(apiKey, config, consent.status === 'resolved' ? consent.effective : null, 'respect');
    return (_jsx(PostHogReadyContext.Provider, { value: ready, children: _jsx(BasePostHogProvider, { client: posthog, children: children }) }));
}
function ConsentExemptProvider({ children, apiKey, config }) {
    const ready = usePostHogLifecycle(apiKey, config, 'granted', 'exempt');
    return (_jsx(PostHogReadyContext.Provider, { value: ready, children: _jsx(BasePostHogProvider, { client: posthog, children: children }) }));
}
export function AnalyticsProvider({ children, apiKey, host, appName, enabled = true, consentMode = 'respect', }) {
    if (!apiKey || !enabled) {
        return _jsx(ConsentModeContext.Provider, { value: consentMode, children: children });
    }
    const config = { apiKey, host, appName };
    const Provider = consentMode === 'exempt' ? ConsentExemptProvider : ConsentRespectingProvider;
    return (_jsx(ConsentModeContext.Provider, { value: consentMode, children: _jsx(Provider, { apiKey: apiKey, config: config, children: children }) }));
}
//# sourceMappingURL=provider.js.map